import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, jsonb, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { QuizAttempt } from '../../../user-content/tracker/quiz-attempt'
import { Book } from '../../book'
import { Subject } from '../category'
import { LessonQuestion } from './lesson-question'
import { LessonToCompetency } from './lesson-to-competency'
import { LessonToKeyword } from './lesson-to-keyword'
import { LessonToSkillWeight } from './lesson-to-skill-weight'

export const Lesson = pgTable(
  'lesson',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    slug: text().notNull(),
    learningInstruction: text().notNull(),
    learningInstructionAudioKey: text(),
    type: text({ enum: ['DISCOVERY', 'IN_CLASS', 'READ', 'QUIZ'] }).notNull(),
    // Type-specific: READ
    bookId: uuid().references(() => Book.id, { onDelete: 'restrict' }),
    // Type-specific: DISCOVERY/IN_CLASS
    content: jsonb('content'), // Rich text content stored as JSONB for read performance
    guide: jsonb('guide'),
    activitySheetKey: text(),
    // Relationships
    subjectId: uuid()
      .notNull()
      .references(() => Subject.id, { onDelete: 'restrict' }),
    isActive: boolean().notNull().default(true),
    legacyNeonLessonId: uuid(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
  },
  (table) => [
    index('lesson_title_idx').on(table.title),
    index('lesson_slug_idx').on(table.slug),
    index('lesson_type_idx').on(table.type),
    index('lesson_subject_idx').on(table.subjectId),
    index('lesson_book_idx').on(table.bookId),
    index('lesson_learning_instruction_audio_key_idx').on(table.learningInstructionAudioKey),
    index('lesson_activity_sheet_key_idx').on(table.activitySheetKey),
    index('lesson_legacy_neon_lesson_id_idx').on(table.legacyNeonLessonId),
    index('lesson_updated_at_idx').on(table.updatedAt),
    index('lesson_created_at_idx').on(table.createdAt),
    // Composite indexes for common query patterns
    index('lesson_type_subject_idx').on(table.type, table.subjectId),
  ],
)

export const lessonRelations = relations(Lesson, ({ one, many }) => ({
  subject: one(Subject, {
    fields: [Lesson.subjectId],
    references: [Subject.id],
  }),
  book: one(Book, {
    fields: [Lesson.bookId],
    references: [Book.id],
  }),
  questions: many(LessonQuestion),
  lessonToKeywords: many(LessonToKeyword),
  lessonToSkillWeights: many(LessonToSkillWeight),
  lessonToCompetencies: many(LessonToCompetency),
  quizAttempts: many(QuizAttempt),
}))

export const InsertLessonSchema = createInsertSchema(Lesson)
export const UpdateLessonSchema = createUpdateSchema(Lesson).omit({ id: true, legacyNeonLessonId: true }).partial()
export const SelectLessonSchema = createSelectSchema(Lesson)

export type InsertLesson = typeof Lesson.$inferInsert
export type UpdateLesson = z.infer<typeof UpdateLessonSchema>
export type SelectLesson = typeof Lesson.$inferSelect
